import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ColorBlindMode = 'none' | 'deuteranopia' | 'protanopia' | 'tritanopia';

/**
 * Chart color palettes optimized for different types of color vision deficiency.
 * Each palette provides 8 distinguishable colors + semantic colors for status.
 */
export const COLOR_BLIND_PALETTES: Record<ColorBlindMode, {
  chart: string[];
  status: { healthy: string; warning: string; critical: string };
  heatmap: string[];
}> = {
  none: {
    chart: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#ec4899'],
    status: { healthy: '#10b981', warning: '#f59e0b', critical: '#ef4444' },
    heatmap: ['#eff6ff', '#bfdbfe', '#93c5fd', '#60a5fa', '#3b82f6', '#1d4ed8'],
  },
  deuteranopia: {
    // Blue-Orange scale — avoids red-green confusion
    chart: ['#0072B2', '#E69F00', '#56B4E9', '#D55E00', '#CC79A7', '#009E73', '#F0E442', '#000000'],
    status: { healthy: '#0072B2', warning: '#E69F00', critical: '#D55E00' },
    heatmap: ['#f0f0f0', '#c6dbef', '#9ecae1', '#6baed6', '#3182bd', '#08519c'],
  },
  protanopia: {
    // Blue-Yellow scale — avoids red confusion
    chart: ['#0072B2', '#F0E442', '#56B4E9', '#E69F00', '#CC79A7', '#009E73', '#D55E00', '#000000'],
    status: { healthy: '#0072B2', warning: '#F0E442', critical: '#D55E00' },
    heatmap: ['#f0f0f0', '#c6dbef', '#9ecae1', '#6baed6', '#3182bd', '#08519c'],
  },
  tritanopia: {
    // Red-Cyan scale — avoids blue-yellow confusion
    chart: ['#CC3311', '#009988', '#EE7733', '#0077BB', '#EE3377', '#33BBEE', '#BBBBBB', '#000000'],
    status: { healthy: '#009988', warning: '#EE7733', critical: '#CC3311' },
    heatmap: ['#f0f0f0', '#fdd0a2', '#fdae6b', '#fd8d3c', '#e6550d', '#a63603'],
  },
};

/**
 * SVG pattern definitions for chart fills — used alongside colors for
 * double-encoding (color + pattern) so data is distinguishable without color.
 */
export const CHART_PATTERNS = [
  { id: 'pattern-solid', type: 'solid' },
  { id: 'pattern-diagonal', type: 'diagonal' },
  { id: 'pattern-dots', type: 'dots' },
  { id: 'pattern-crosshatch', type: 'crosshatch' },
  { id: 'pattern-horizontal', type: 'horizontal' },
  { id: 'pattern-vertical', type: 'vertical' },
  { id: 'pattern-zigzag', type: 'zigzag' },
  { id: 'pattern-grid', type: 'grid' },
] as const;

interface AccessibilityState {
  colorBlindMode: ColorBlindMode;
  usePatternFills: boolean;
  reducedMotion: boolean;
  highContrast: boolean;
  showDataTables: boolean; // show data table below every chart
  setColorBlindMode: (mode: ColorBlindMode) => void;
  setUsePatternFills: (v: boolean) => void;
  setReducedMotion: (v: boolean) => void;
  setHighContrast: (v: boolean) => void;
  setShowDataTables: (v: boolean) => void;
  getChartColors: () => string[];
  getStatusColors: () => { healthy: string; warning: string; critical: string };
  getHeatmapColors: () => string[];
}

export const useAccessibilityStore = create<AccessibilityState>()(
  persist(
    (set, get) => ({
      colorBlindMode: 'none',
      usePatternFills: false,
      reducedMotion: typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false,
      highContrast: false,
      showDataTables: false,

      setColorBlindMode: (mode) => {
        // Only change the palette. Pattern fills stay under the user's explicit
        // control via its own toggle (no auto-enable on mode change).
        set({ colorBlindMode: mode });
      },
      setUsePatternFills: (v) => set({ usePatternFills: v }),
      setReducedMotion: (v) => set({ reducedMotion: v }),
      setHighContrast: (v) => set({ highContrast: v }),
      setShowDataTables: (v) => set({ showDataTables: v }),

      getChartColors: () => COLOR_BLIND_PALETTES[get().colorBlindMode].chart,
      getStatusColors: () => COLOR_BLIND_PALETTES[get().colorBlindMode].status,
      getHeatmapColors: () => COLOR_BLIND_PALETTES[get().colorBlindMode].heatmap,
    }),
    { name: 'accessibility-storage' },
  ),
);
